import api from './api';

export interface UploadImageParams {
  workspaceId: string;
  file: File;
  entityType?: string;
  entityId?: string;
  imageType?: string;
  title?: string;
  description?: string;
  caption?: string;
}

export interface UploadedImage {
  _id: string;
  url: string;
  key: string;
  imageType: string;
  size: number;
  mimeType: string;
  description?: string;
}

export interface UploadImageResponse {
  message: string;
  image: UploadedImage;
}

export interface FloorLayoutImage {
  url: string;
  key: string;
  width: number;
  height: number;
  size: number;
  mimeType: string;
}

/**
 * Upload an image file to S3 via the API
 */
export const uploadImage = async (params: UploadImageParams): Promise<UploadedImage> => {
  const {
    workspaceId,
    file,
    entityType,
    entityId,
    imageType = 'photo',
    title,
    description,
    caption
  } = params;

  const formData = new FormData();
  formData.append('file', file);

  if (entityType) formData.append('entityType', entityType);
  if (entityId) formData.append('entityId', entityId);
  if (imageType) formData.append('imageType', imageType);
  if (title) formData.append('title', title);
  if (description) formData.append('description', description);
  if (caption) formData.append('caption', caption);


  const response = await api.post<UploadImageResponse>(
    `/workspaces/${workspaceId}/upload/image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return response.data.image;
};

/**
 * Delete an image from S3 and database
 */
export const deleteImage = async (workspaceId: string, imageId: string): Promise<void> => {

  await api.delete(
    `/workspaces/${workspaceId}/upload/image/${imageId}`,
    {
      headers: {
      }
    }
  );
};

/**
 * Get presigned URL for an image
 */
export const getImageUrl = async (workspaceId: string, imageId: string): Promise<string> => {

  const response = await api.get(
    `/workspaces/${workspaceId}/images/${imageId}/url`,
    {
      headers: {
      }
    }
  );

  return response.data.url;
};

/**
 * Get images for an entity with presigned URLs
 */
export const getImages = async (
  workspaceId: string,
  filters?: {
    entityType?: string;
    entityId?: string;
    imageType?: string;
  }
): Promise<UploadedImage[]> => {

  const params = new URLSearchParams();
  if (filters?.entityType) params.append('entityType', filters.entityType);
  if (filters?.entityId) params.append('entityId', filters.entityId);
  if (filters?.imageType) params.append('imageType', filters.imageType);

  const response = await api.get(
    `/workspaces/${workspaceId}/images?${params.toString()}`,
    {
      headers: {
      }
    }
  );

  const images = response.data.images;

  // Fetch presigned URLs for each image
  const imagesWithUrls = await Promise.all(
    images.map(async (image: UploadedImage) => {
      const url = await getImageUrl(workspaceId, image._id);
      return { ...image, url };
    })
  );

  return imagesWithUrls;
};

/**
 * Update image description
 */
export const updateImageDescription = async (
  workspaceId: string,
  imageId: string,
  description: string
): Promise<void> => {

  await api.put(
    `/workspaces/${workspaceId}/images/${imageId}`,
    { description },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
};

/**
 * Update image order
 */
export const updateImageOrder = async (
  workspaceId: string,
  imageId: string,
  order: number
): Promise<void> => {

  await api.put(
    `/workspaces/${workspaceId}/images/${imageId}`,
    { order },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
};

/**
 * Bulk update image order - more efficient for reordering multiple images
 */
export const bulkUpdateImageOrder = async (
  workspaceId: string,
  updates: Array<{ imageId: string; order: number }>
): Promise<void> => {
  await api.put(
    `/workspaces/${workspaceId}/images/bulk-update-order`,
    { updates },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );
};

/**
 * Upload floor layout image
 */
export const uploadFloorLayout = async (
  workspaceId: string,
  file: File,
  entityType: 'parking' | 'bicycle' | 'locker',
  floorId: string
): Promise<FloorLayoutImage> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entityType', `${entityType}_floor`);
  formData.append('entityId', floorId);
  formData.append('imageType', `${entityType}_layout`);


  const response = await api.post<UploadImageResponse>(
    `/workspaces/${workspaceId}/upload/image`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  const image = response.data.image;
  return {
    url: image.url,
    key: image.key,
    width: 0, // Will be set by image onload
    height: 0, // Will be set by image onload
    size: image.size,
    mimeType: image.mimeType
  };
};

export interface UploadedVideo {
  path: string;
  key: string;
  url: string;
  size: number;
  mimeType: string;
}

/**
 * Upload video file to S3
 */
export const uploadVideo = async (
  workspaceId: string,
  entityId: string,
  file: File,
  videoType: string = 'intro-video',
  entityType: string = 'project',
  onProgress?: (progress: number) => void
): Promise<UploadedVideo> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('entityType', entityType);
  formData.append('entityId', entityId);
  formData.append('videoType', videoType);

  const response = await api.post<{ message: string; video: { url: string; key: string; path: string; size: number; mimeType: string } }>(
    `/workspaces/${workspaceId}/upload/video`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    }
  );

  return response.data.video;
};
